/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {Fragment, useRef, useState} from "react";
import {Listbox} from "@headlessui/react";
import {ArrowsUpDownIcon, CheckIcon} from "@heroicons/react/24/solid";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {Overlay} from "react-overlays";
import {Option} from "types/entities/specific/Option";

interface Props {
    options: Option[];
    selected: number | string | null;
    setSelected: (string) => void;
    label: string;
    disabled?: boolean;
}

const SelectBox = (props: Props) => {
    const {t} = useTranslation();
    const selectedOptionRef = useRef<HTMLLIElement>(null);
    const targetRef = useRef(null);
    const [show, setShow] = useState(false);
    const options = props?.options ?? [];
    const dropdownHeightByRowCount = props.options.length * 28 + 10;
    const dropdownHeight = dropdownHeightByRowCount > 240 ? 240 : dropdownHeightByRowCount;
    // const selected = props.options.find(option => option.value == props.selected) || {
    //     label: "Select from options",
    //     value: null,
    // };
    const selected = props.options.find(option => option.value == props.selected);

    const setSelectedHandler = newSelected => {
        props.setSelected(newSelected);
        setShow(false);
    };
    return (
        <div className={"flex flex-row relative flex-1"}>
            <Listbox value={selected} onChange={setSelectedHandler}>
                {({open}) => (
                    <div className={clsx("SelectBox w-full")}>
                        <Listbox.Button
                            ref={targetRef}
                            className={clsx(
                                "z-10 h-full flex items-center text-base justify-between w-full px-2 lg:px-3 py-1.5 lg:py-2 rounded-md border-gray-200 border",
                                props.disabled
                                    ? selected
                                        ? "bg-gray-100 text-gray-600"
                                        : "bg-gray-100 text-gray-400"
                                    : selected
                                      ? "bg-white text-black"
                                      : "bg-white text-gray-400",
                                selected ? "text-black border-black" : "text-gray-400"
                            )}
                            onClick={() => setShow(!show)}
                        >
                            <div className={"flex flex-row items-center overflow-hidden w-full justify-between"}>
                                <span className={"truncate capitalize overflow-hidden whitespace-nowrap"}>
                                    {selected?.label ? selected.label : t("Select")}
                                </span>
                                <span className="pointer-events-none pl-1 sm:pl-1.5 lg:pl-2">
                                    <ArrowsUpDownIcon aria-hidden="true" className="h-3 w-3 sm:h-4 sm:w-4 sm:lg:h-5 lg:w-5" />
                                </span>
                            </div>
                        </Listbox.Button>

                        <Overlay placement="bottom-start" show={show} target={targetRef.current} rootClose onHide={() => setShow(false)}>
                            {({props, placement}) => (
                                <Listbox.Options
                                    {...props}
                                    className={clsx(
                                        "transition-all duration-300 ease-in h-auto drop-shadow shadow-sm z-10 w-full max-w-full rounded-md bg-white overflow-x-hidden py-1 text-base",
                                        placement
                                    )}
                                    style={{
                                        maxHeight: dropdownHeight + "px",
                                        ...props.style,
                                        transition: "maxHeight 0.3s ease-in-out",
                                        // @ts-ignore
                                        width: targetRef?.current ? targetRef.current.getBoundingClientRect().width : "100%",
                                    }}
                                    unmount={false}
                                    onLoad={() => {
                                        if (selected && selectedOptionRef?.current) {
                                            selectedOptionRef.current.scrollIntoView({
                                                behavior: "smooth",
                                                block: "nearest",
                                            });
                                        }
                                    }}
                                >
                                    {options.map(option => (
                                        <Listbox.Option key={option.value} as={Fragment} value={option}>
                                            {({selected, active}) => (
                                                <li
                                                    className={clsx(
                                                        "flex flex-row cursor-pointer select-none py-1 pr-0 text-sm w-full",
                                                        active || selected ? "bg-gray-200/80 text-black" : ""
                                                    )}
                                                >
                                                    {selected && (
                                                        <span className={"w-auto flex items-center"}>
                                                            <CheckIcon className={"w-[16px] h-[16px] flex-none ml-[3px] mr-[3px] text-arbo-red"} />
                                                        </span>
                                                    )}
                                                    <div className={clsx("flex items-center truncate w-full", !selected && "pl-[22px]")}>
                                                        <span className={clsx("block truncate")}>{option.label}</span>
                                                    </div>
                                                </li>
                                            )}
                                        </Listbox.Option>
                                    ))}
                                </Listbox.Options>
                            )}
                        </Overlay>
                    </div>
                )}
            </Listbox>
        </div>
    );
};

export default SelectBox;
